import { NotifyHelper } from 'classes/helpers/notify.helper';
import { t } from 'i18next';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { StateRoutes } from 'lib_ts/enums/routing/routes/state';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import {
  IBuildPitchPayload,
  IBuildPitchRequest,
  IBuildResultDict,
  IClosedLoopBuildChars,
  IClosedLoopBuildRequest,
  IClosedLoopBuildResult,
} from 'lib_ts/interfaces/pitches';
import {
  IBuildMsRequest,
  IBuildMsResult,
} from 'lib_ts/interfaces/pitches/i-build-ms';
import { IManualShot } from 'lib_ts/interfaces/training/i-manual-shot';
import { compact } from 'lodash';
import { BaseRESTService } from 'services/_base-rest.service';

export class StateTransformService extends BaseRESTService {
  private static instance: StateTransformService;
  static getInstance(): StateTransformService {
    if (!StateTransformService.instance) {
      StateTransformService.instance = new StateTransformService();
    }

    return StateTransformService.instance;
  }

  private constructor() {
    super({
      controller: Controller.State,
    });
  }

  /** encapsulated logic for taking ball chars and positions to build pitches
   * to be used for all add/test/train pitch instances
   * */
  async buildMany(config: {
    machine: IMachine;
    notifyError: boolean;
    chars: IBuildPitchPayload[];
  }): Promise<IBuildResultDict | undefined> {
    try {
      if (config.chars.length === 0) {
        return {};
      }

      const withoutPriority = config.chars.filter((p) => !p.priority);
      if (withoutPriority.length > 0) {
        console.error(withoutPriority);
        throw new Error(
          `Cannot build ${withoutPriority.length} pitches without priority`
        );
      }

      // ensure seams exists on all chars (if possible)
      config.chars
        .filter((c) => !c.seams)
        .forEach((c) => {
          if (!c.bs) {
            return;
          }

          c.seams = BallHelper.getSeamsFromBS(c.bs);
        });

      const payload: IBuildPitchRequest = {
        machineID: config.machine.machineID,
        ball_type: config.machine.ball_type,
        pitches: config.chars,
      };

      // results will already be rotated by the server to point at given plate locations in the payload
      const results: IBuildResultDict = await this.post(
        { uri: StateRoutes.BuildMany },
        payload
      );

      const errors = compact(Object.values(results).map((m) => m?.error));

      if (errors.length > 0) {
        if (config.notifyError) {
          NotifyHelper.warning({
            message_md: t('pu.build-pitch-error-msg'),
          });
        }

        console.error({
          event: 'STATE TRANSFORM: error(s) while building pitches',
          errors: errors,
        });
      }

      return results;
    } catch (e) {
      console.error(e);

      if (config.notifyError) {
        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an unknown error while building ${
                  config.chars.length === 1 ? 'pitch' : 'pitches'
                }. ${ERROR_MSGS.CONTACT_SUPPORT}}`,
        });
      }

      return undefined;
    }
  }

  async buildManyClosedLoop(config: {
    machineID: string;
    pitches: IClosedLoopBuildChars[];
    notifyError: boolean;
    stepSize: number;
  }): Promise<IClosedLoopBuildChars[]> {
    try {
      // ensure seams exists on all chars (if possible)
      config.pitches
        .filter((c) => !c.seams)
        .forEach((c) => {
          if (!c.target_bs) {
            return;
          }

          c.seams = BallHelper.getSeamsFromBS(c.target_bs);
        });

      const payload: IClosedLoopBuildRequest = {
        pitches: config.pitches,
        step_size: config.stepSize,
      };

      const results: IClosedLoopBuildResult = await this.post(
        { uri: StateRoutes.BuildManyClosedLoop },
        payload
      );

      if (results.errors.length > 0) {
        if (config.notifyError) {
          NotifyHelper.warning({
            message_md: t('pu.build-pitch-error-msg'),
          });
        }

        console.error({
          event:
            'STATE TRANSFORM: error(s) while building pitches with closed loop',
          errors: results.errors,
        });
      }

      return results.pitches;
    } catch (e) {
      console.error(e);

      if (config.notifyError) {
        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an unknown error while building ${
                  config.pitches.length === 1 ? 'pitch' : 'pitches'
                } with closed loop. ${ERROR_MSGS.CONTACT_SUPPORT}}`,
        });
      }

      return [];
    }
  }

  async detectManual(payload: IManualShot): Promise<boolean> {
    return await this.post({ uri: StateRoutes.DetectManual }, payload)
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
        }

        return result.success;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return false;
      });
  }

  async buildMs(config: {
    machineID: string;
    ms: IMachineState;
  }): Promise<IBuildMsResult | undefined> {
    try {
      const payload: IBuildMsRequest = {
        machineID: config.machineID,
        ms: config.ms,
      };

      // results will already be rotated by the server to point at given plate locations in the payload
      const results: IBuildMsResult = await this.post(
        { uri: StateRoutes.BuildMs },
        payload
      );

      return results;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md:
          'Failed to build ball state and trajectory from machine state.',
      });

      return undefined;
    }
  }
}
